// Estimating workspace tabs for the Takeoff Project. All tabs edit the
// takeoff doc via patch() — the workspace autosave persists everything.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Trash2, AlertTriangle, Trophy, Loader2, ArrowUp, ArrowDown, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtQty, nzd } from "@/lib/takeoffGeo";
import { Panel } from "@/components/TakeoffParts";

const uid = () => crypto.randomUUID();
const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// ---------- shared estimate maths ----------
export function itemValue(m) {
  const waste = 1 + ((m.waste_pct || 0) / 100);
  let v = (m.qty || 0) * waste * ((m.rate?.unit_rate) || 0);
  (m.derived || []).forEach((d) => { v += (d.qty || 0) * (d.rate || 0); });
  return v;
}

export function computeEstimate(t) {
  const es = t.estimate || {};
  const layers = t.layers || [];
  const byDiscipline = {};
  let direct = 0;
  (t.measurements || []).forEach((m) => {
    const v = itemValue(m);
    const name = layers.find((l) => l.id === m.layer_id)?.name || "Other";
    byDiscipline[name] = (byDiscipline[name] || 0) + v;
    direct += v;
  });
  const riskAllow = es.include_risks === false ? 0
    : (t.risks || []).reduce((s, r) => s + (r.allowance || 0), 0);
  const contingency = direct * ((es.contingency_pct || 0) / 100);
  const overhead = (direct + riskAllow + contingency) * ((es.overhead_pct || 0) / 100);
  const cost = direct + riskAllow + contingency + overhead;
  const mPct = (es.margin_pct ?? 10) / 100;
  const sell = es.margin_mode === "margin" && mPct < 1 ? cost / (1 - mPct) : cost * (1 + mPct);
  return { byDiscipline, direct, riskAllow, contingency, overhead, cost, sell, es };
}

const Num = ({ label, value, onChange, suffix, step = 1, w = "w-24" }) => (
  <label className="flex items-center gap-2 text-xs">
    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex-1">{label}</span>
    <div className="relative">
      <Input type="number" step={step} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
        className={`h-8 rounded-sm text-xs font-mono pr-7 ${w}`} />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400">{suffix}</span>}
    </div>
  </label>
);

// ================= ESTIMATE TAB =================
export function EstimateTab({ t, patch }) {
  const est = computeEstimate(t);
  const es = t.estimate || {};
  const setEs = (ch) => patch({ estimate: { ...es, ...ch } });
  const total = Object.values(est.byDiscipline).reduce((s, v) => s + v, 0) || 1;

  // advisory coverage check
  const issues = useMemo(() => {
    const out = [];
    const noRate = (t.measurements || []).filter((m) => !(m.rate?.unit_rate) && itemValue(m) === 0);
    if (noRate.length) out.push(`${noRate.length} measurement(s) have no rate`);
    const uncal = (t.drawings || []).filter((d) => !Object.keys(d.scales || {}).length);
    if (uncal.length) out.push(`${uncal.length} drawing(s) not calibrated`);
    const derNoRate = (t.measurements || []).flatMap((m) => (m.derived || []).filter((d) => d.qty && !d.rate));
    if (derNoRate.length) out.push(`${derNoRate.length} derived quantit${derNoRate.length > 1 ? "ies" : "y"} (bedding/backfill/spoil…) unpriced`);
    const riskNoAllow = (t.risks || []).filter((r) => r.probability === "high" && !r.allowance);
    if (riskNoAllow.length) out.push(`${riskNoAllow.length} high-probability risk(s) with no cost allowance`);
    if (!(t.qnotes || []).some((n) => n.kind === "exclusion")) out.push("No exclusions recorded yet");
    return out;
  }, [t]);

  const snapshot = () => {
    const label = window.prompt("Version label", `V${(t.versions || []).length + 1}`);
    if (!label) return;
    patch({
      versions: [...(t.versions || []), {
        id: uid(), label, at: new Date().toISOString(),
        direct: r2(est.direct), risk: r2(est.riskAllow + est.contingency),
        overhead: r2(est.overhead), sell: r2(est.sell),
        item_count: (t.measurements || []).length,
        by_discipline: Object.fromEntries(Object.entries(est.byDiscipline).map(([k, v]) => [k, r2(v)])),
      }],
    });
    toast.success(`Snapshot "${label}" saved`);
  };

  const scenarios = [8, 10, 12, 15];
  return (
    <div className="grid lg:grid-cols-3 gap-3 p-3 max-w-6xl" data-testid="takeoff-estimate-tab">
      <Panel title="Estimate Summary" testId="estimate-summary-panel">
        <div className="p-3 space-y-1 text-xs font-mono">
          {[["Direct cost (measured + waste)", est.direct],
            ["Risk allowances", est.riskAllow],
            ["Contingency", est.contingency],
            ["Overhead", est.overhead],
            ["Cost before margin", est.cost]].map(([l, v]) => (
            <div key={l} className="flex justify-between"><span className="text-zinc-500">{l}</span><b>{nzd(v)}</b></div>
          ))}
          <div className="flex justify-between pt-2 border-t-2 border-zinc-200 dark:border-white/10 text-sm">
            <span className="font-bold uppercase tracking-wider">Sell price (ex GST)</span>
            <b className="text-[#FF5F15] text-base" data-testid="estimate-sell-price">{nzd(est.sell)}</b>
          </div>
        </div>
        <div className="px-3 pb-3 space-y-2">
          <Num label="Overhead %" value={es.overhead_pct} suffix="%" step={0.5} onChange={(v) => setEs({ overhead_pct: v })} />
          <Num label="Contingency %" value={es.contingency_pct} suffix="%" step={0.5} onChange={(v) => setEs({ contingency_pct: v })} />
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex-1">
              {es.margin_mode === "margin" ? "Margin %" : "Markup %"}
            </span>
            <button onClick={() => setEs({ margin_mode: es.margin_mode === "margin" ? "markup" : "margin" })}
              className="text-[9px] font-bold uppercase tracking-wider text-[#FF5F15]" data-testid="estimate-margin-mode">
              switch to {es.margin_mode === "margin" ? "markup" : "margin"}
            </button>
            <Input type="number" step={0.5} value={es.margin_pct ?? 10}
              onChange={(e) => setEs({ margin_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
              className="h-8 rounded-sm text-xs font-mono w-24" data-testid="estimate-margin-input" />
          </div>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" checked={es.include_risks !== false} onChange={(e) => setEs({ include_risks: e.target.checked })} className="accent-[#FF5F15]" />
            Include risk register allowances ({nzd((t.risks || []).reduce((s, r) => s + (r.allowance || 0), 0))})
          </label>
        </div>
      </Panel>

      <Panel title="By Discipline" testId="estimate-discipline-panel">
        <div className="divide-y divide-zinc-100 dark:divide-white/5">
          {Object.entries(est.byDiscipline).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="px-3 py-2">
              <div className="flex justify-between text-xs"><b>{k}</b><span className="font-mono">{nzd(v)} <span className="text-zinc-400">({Math.round((v / total) * 100)}%)</span></span></div>
              <div className="h-1 bg-zinc-200 dark:bg-white/10 mt-1"><div className="h-full bg-[#FF5F15]" style={{ width: `${(v / total) * 100}%` }} /></div>
            </div>
          ))}
          {!Object.keys(est.byDiscipline).length && <p className="px-3 py-4 text-xs text-zinc-500">Measure and rate items on the Takeoff tab first.</p>}
        </div>
        <div className="px-3 py-2 border-t border-zinc-200 dark:border-white/10">
          <div className="label-tag mb-1.5">Margin scenarios</div>
          <div className="grid grid-cols-4 gap-1.5">
            {scenarios.map((p) => {
              const s = es.margin_mode === "margin" ? est.cost / (1 - p / 100) : est.cost * (1 + p / 100);
              return (
                <button key={p} onClick={() => setEs({ margin_pct: p })}
                  className={`p-1.5 border text-center ${es.margin_pct === p ? "border-[#FF5F15] text-[#FF5F15]" : "border-zinc-200 dark:border-white/10 hover:border-zinc-400"}`}
                  data-testid={`estimate-scenario-${p}`}>
                  <div className="text-[10px] font-bold">{p}%</div>
                  <div className="text-[9px] font-mono text-zinc-500">{nzd(s)}</div>
                </button>
              );
            })}
          </div>
          <p className="text-[9px] text-zinc-400 mt-1">Click a scenario to apply it.</p>
        </div>
      </Panel>

      <div className="space-y-3">
        <Panel title="Check Estimate" icon={AlertTriangle} testId="estimate-check-panel">
          <div className="p-3 space-y-1.5">
            {issues.length === 0 ? (
              <p className="text-xs text-emerald-500 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> No gaps found — advisory check only.</p>
            ) : issues.map((i, n) => (
              <p key={n} className="text-[11px] flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" /> {i}</p>
            ))}
            <p className="text-[9px] text-zinc-400 pt-1">Advisory only — the estimator makes the final call.</p>
          </div>
        </Panel>
        <Panel title={`Versions (${(t.versions || []).length})`}
          action={<Button size="sm" variant="ghost" onClick={snapshot} className="h-6 px-2 text-[10px] font-bold uppercase text-[#FF5F15]" data-testid="estimate-snapshot-btn"><Plus className="w-3 h-3 mr-1" /> Snapshot</Button>}
          testId="estimate-versions-panel">
          <div className="divide-y divide-zinc-100 dark:divide-white/5 max-h-56 overflow-y-auto">
            {(t.versions || []).slice().reverse().map((v, i, arr) => {
              const prev = arr[i + 1];
              return (
                <div key={v.id} className="px-3 py-2 text-[11px]">
                  <div className="flex justify-between"><b>{v.label}</b><span className="font-mono">{nzd(v.sell)}</span></div>
                  <div className="text-[9px] font-mono text-zinc-500 flex justify-between">
                    <span>{new Date(v.at).toLocaleDateString("en-NZ")} · {v.item_count} items</span>
                    {prev && <span className={v.sell - prev.sell >= 0 ? "text-amber-500" : "text-emerald-500"}>{v.sell - prev.sell >= 0 ? "+" : ""}{nzd(v.sell - prev.sell)} vs {prev.label}</span>}
                  </div>
                </div>
              );
            })}
            {!(t.versions || []).length && <p className="px-3 py-3 text-xs text-zinc-500">Snapshot the estimate before big changes (Tender V1, BAFO…).</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ================= RISKS TAB =================
const LEVELS = ["low", "medium", "high"];
const riskScore = (r) => LEVELS.indexOf(r.probability || "low") + LEVELS.indexOf(r.impact || "low");
const RISK_TONE = ["text-emerald-500", "text-emerald-500", "text-amber-500", "text-amber-500", "text-red-500"];

export function RisksTab({ t, patch }) {
  const risks = t.risks || [];
  const set = (id, ch) => patch({ risks: risks.map((r) => (r.id === id ? { ...r, ...ch } : r)) });
  const add = () => patch({ risks: [...risks, { id: uid(), title: "", category: "", probability: "medium", impact: "medium", allowance: null, owner: "", status: "open" }] });
  const Sel = ({ v, onCh, testid }) => (
    <select value={v} onChange={(e) => onCh(e.target.value)} data-testid={testid}
      className="h-7 rounded-sm text-[11px] bg-transparent border border-zinc-300 dark:border-white/15 px-1 dark:bg-[#0F141C] capitalize">
      {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
    </select>
  );
  return (
    <div className="p-3 max-w-5xl" data-testid="takeoff-risks-tab">
      <Panel title={`Tender Risk Register (${risks.length})`}
        action={<Button size="sm" variant="ghost" onClick={add} className="h-6 px-2 text-[10px] font-bold uppercase text-[#FF5F15]" data-testid="risks-add-btn"><Plus className="w-3 h-3 mr-1" /> Add risk</Button>}>
        <div className="divide-y divide-zinc-100 dark:divide-white/5">
          {risks.map((r) => (
            <div key={r.id} className="px-3 py-2 grid grid-cols-12 gap-2 items-center" data-testid={`risk-row-${r.id}`}>
              <Input value={r.title} placeholder="e.g. Groundwater" onChange={(e) => set(r.id, { title: e.target.value })} className="h-7 rounded-sm text-xs col-span-3" data-testid={`risk-title-${r.id}`} />
              <Input value={r.category || ""} placeholder="Category" onChange={(e) => set(r.id, { category: e.target.value })} className="h-7 rounded-sm text-[11px] col-span-2" />
              <Sel v={r.probability} onCh={(v) => set(r.id, { probability: v })} testid={`risk-prob-${r.id}`} />
              <Sel v={r.impact} onCh={(v) => set(r.id, { impact: v })} testid={`risk-impact-${r.id}`} />
              <span className={`text-[10px] font-bold uppercase text-center ${RISK_TONE[riskScore(r)]}`}>{["low", "low", "med", "high", "high"][riskScore(r)]}</span>
              <div className="relative col-span-2">
                <Input type="number" value={r.allowance ?? ""} placeholder="Allowance"
                  onChange={(e) => set(r.id, { allowance: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  className="h-7 rounded-sm text-[11px] font-mono pl-5" data-testid={`risk-allowance-${r.id}`} />
                <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400">$</span>
              </div>
              <Input value={r.owner || ""} placeholder="Owner" onChange={(e) => set(r.id, { owner: e.target.value })} className="h-7 rounded-sm text-[11px]" />
              <button onClick={() => patch({ risks: risks.filter((x) => x.id !== r.id) })} className="text-zinc-400 hover:text-red-500 justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {!risks.length && <p className="px-3 py-5 text-xs text-zinc-500">Groundwater, rock, unknown services, access, night works… record them here with an allowance and they feed the estimate.</p>}
        </div>
        <div className="px-3 py-2 border-t border-zinc-200 dark:border-white/10 flex justify-between text-xs">
          <span className="text-zinc-500 uppercase font-bold tracking-wider text-[10px]">Total allowances</span>
          <b className="font-mono" data-testid="risks-total">{nzd(risks.reduce((s, r) => s + (r.allowance || 0), 0))}</b>
        </div>
      </Panel>
    </div>
  );
}

// ================= RFIs TAB =================
export function RfisTab({ t, patch }) {
  const rfis = t.rfis || [];
  const set = (id, ch) => patch({ rfis: rfis.map((r) => (r.id === id ? { ...r, ...ch } : r)) });
  const add = () => patch({ rfis: [...rfis, { id: uid(), number: `RFI ${String(rfis.length + 1).padStart(3, "0")}`, title: "", due_date: "", status: "open", response: "", created_at: new Date().toISOString() }] });
  return (
    <div className="p-3 max-w-4xl" data-testid="takeoff-rfis-tab">
      <Panel title={`RFIs (${rfis.length})`}
        action={<Button size="sm" variant="ghost" onClick={add} className="h-6 px-2 text-[10px] font-bold uppercase text-[#FF5F15]" data-testid="rfis-add-btn"><Plus className="w-3 h-3 mr-1" /> New RFI</Button>}>
        <div className="divide-y divide-zinc-100 dark:divide-white/5">
          {rfis.map((r) => (
            <div key={r.id} className="px-3 py-2.5 space-y-1.5" data-testid={`rfi-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] font-bold text-[#FF5F15] shrink-0">{r.number}</span>
                <Input value={r.title} placeholder="e.g. Confirm invert level at MH105" onChange={(e) => set(r.id, { title: e.target.value })} className="h-7 rounded-sm text-xs flex-1" data-testid={`rfi-title-${r.id}`} />
                <Input type="date" value={r.due_date || ""} onChange={(e) => set(r.id, { due_date: e.target.value })} className="h-7 rounded-sm text-[11px] w-32" />
                <select value={r.status} onChange={(e) => set(r.id, { status: e.target.value })}
                  className={`h-7 rounded-sm text-[10px] font-bold uppercase bg-transparent border px-1 dark:bg-[#0F141C] ${r.status === "answered" ? "border-emerald-500/50 text-emerald-500" : "border-amber-500/50 text-amber-500"}`}>
                  <option value="open">Open</option><option value="sent">Sent</option><option value="answered">Answered</option>
                </select>
                <button onClick={() => patch({ rfis: rfis.filter((x) => x.id !== r.id) })} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <Input value={r.response || ""} placeholder="Response / answer…" onChange={(e) => set(r.id, { response: e.target.value })} className="h-7 rounded-sm text-[11px]" />
            </div>
          ))}
          {!rfis.length && <p className="px-3 py-5 text-xs text-zinc-500">Track tender questions here — invert levels, service clashes, spec clarifications.</p>}
        </div>
      </Panel>
    </div>
  );
}

// ================= NOTES + CHECKLIST TAB =================
const NOTE_KINDS = [
  { key: "assumption", label: "Assumption", tone: "text-sky-400 border-sky-500/40" },
  { key: "exclusion", label: "Exclusion", tone: "text-amber-400 border-amber-500/40" },
  { key: "clarification", label: "Clarification", tone: "text-violet-400 border-violet-500/40" },
  { key: "note", label: "Note", tone: "text-zinc-400 border-zinc-500/40" },
];
const CHECKLIST = [
  "Plans reviewed", "Specifications reviewed", "Geotech reviewed", "Site visit completed",
  "Supplier prices received", "Subcontractor quotes received", "RFIs reviewed", "Addenda reviewed",
  "Risks reviewed", "Programme reviewed", "Estimate checked", "Margin approved", "Quote ready",
];

export function NotesTab({ t, patch }) {
  const notes = t.qnotes || [];
  const [text, setText] = useState("");
  const [kind, setKind] = useState("assumption");
  const cl = t.checklist || {};
  const done = CHECKLIST.filter((k) => cl[k]).length;
  const add = () => {
    if (!text.trim()) return;
    patch({ qnotes: [...notes, { id: uid(), kind, text: text.trim(), created_at: new Date().toISOString() }] });
    setText("");
  };
  return (
    <div className="grid lg:grid-cols-2 gap-3 p-3 max-w-5xl" data-testid="takeoff-notes-tab">
      <Panel title={`Assumptions / Exclusions / Notes (${notes.length})`}>
        <div className="p-3 flex gap-1.5">
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="h-8 rounded-sm text-[11px] bg-transparent border border-zinc-300 dark:border-white/15 px-1 dark:bg-[#0F141C]" data-testid="notes-kind-select">
            {NOTE_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Rock excavation excluded unless noted" className="h-8 rounded-sm text-xs flex-1" data-testid="notes-text-input" />
          <Button size="sm" onClick={add} className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase" data-testid="notes-add-btn">Add</Button>
        </div>
        <div className="divide-y divide-zinc-100 dark:divide-white/5 max-h-[50vh] overflow-y-auto">
          {notes.map((n) => {
            const k = NOTE_KINDS.find((x) => x.key === n.kind) || NOTE_KINDS[3];
            return (
              <div key={n.id} className="px-3 py-2 flex items-start gap-2 group" data-testid={`note-row-${n.id}`}>
                <span className={`px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider border shrink-0 ${k.tone}`}>{k.label}</span>
                <span className="text-xs flex-1">{n.text}</span>
                <button onClick={() => patch({ qnotes: notes.filter((x) => x.id !== n.id) })} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </div>
            );
          })}
        </div>
        <p className="px-3 py-2 text-[10px] text-zinc-500 border-t border-zinc-100 dark:border-white/5">Assumptions, exclusions and clarifications print onto the quote automatically.</p>
      </Panel>
      <Panel title={`Tender Checklist — ${Math.round((done / CHECKLIST.length) * 100)}%`} testId="checklist-panel">
        <div className="h-1 bg-zinc-200 dark:bg-white/10"><div className="h-full bg-[#FF5F15] transition-all" style={{ width: `${(done / CHECKLIST.length) * 100}%` }} /></div>
        <div className="p-3 grid grid-cols-1 gap-1">
          {CHECKLIST.map((k) => (
            <label key={k} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
              <input type="checkbox" checked={!!cl[k]} onChange={(e) => patch({ checklist: { ...cl, [k]: e.target.checked } })}
                className="accent-[#FF5F15]" data-testid={`checklist-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              <span className={cl[k] ? "line-through text-zinc-400" : ""}>{k}</span>
            </label>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ================= PROGRAMME TAB =================
export function ProgrammeTab({ t, patch }) {
  const rows = (t.programme || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const set = (id, ch) => patch({ programme: rows.map((r) => (r.id === id ? { ...r, ...ch } : r)) });
  const add = () => patch({ programme: [...rows, { id: uid(), phase: "", qty: null, unit: "m", production: null, crew: "", plant: "", order: rows.length }] });
  const move = (i, dir) => {
    const next = [...rows];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    patch({ programme: next.map((r, n) => ({ ...r, order: n })) });
  };
  const dur = (r) => (r.qty && r.production ? r.qty / r.production : null);
  const totalDays = rows.reduce((s, r) => s + (dur(r) || 0), 0);
  return (
    <div className="p-3 max-w-5xl" data-testid="takeoff-programme-tab">
      <Panel title="Tender Programme (sequential)"
        action={<Button size="sm" variant="ghost" onClick={add} className="h-6 px-2 text-[10px] font-bold uppercase text-[#FF5F15]" data-testid="programme-add-btn"><Plus className="w-3 h-3 mr-1" /> Add phase</Button>}>
        <div className="px-3 py-1.5 grid grid-cols-12 gap-2 text-[9px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
          <span className="col-span-3">Phase</span><span className="col-span-2">Qty</span><span>Unit</span>
          <span className="col-span-2">Production /day</span><span className="col-span-2">Crew / plant</span><span>Days</span><span />
        </div>
        <div className="divide-y divide-zinc-100 dark:divide-white/5">
          {rows.map((r, i) => (
            <div key={r.id} className="px-3 py-1.5 grid grid-cols-12 gap-2 items-center" data-testid={`programme-row-${r.id}`}>
              <div className="col-span-3 flex items-center gap-1">
                <span className="font-mono text-[10px] text-zinc-400 w-4">{i + 1}</span>
                <Input value={r.phase} placeholder="e.g. Stormwater" onChange={(e) => set(r.id, { phase: e.target.value })} className="h-7 rounded-sm text-xs" />
              </div>
              <Input type="number" value={r.qty ?? ""} onChange={(e) => set(r.id, { qty: e.target.value === "" ? null : parseFloat(e.target.value) })} className="h-7 rounded-sm text-[11px] font-mono col-span-2" data-testid={`programme-qty-${r.id}`} />
              <Input value={r.unit || ""} onChange={(e) => set(r.id, { unit: e.target.value })} className="h-7 rounded-sm text-[11px] w-14" />
              <Input type="number" value={r.production ?? ""} onChange={(e) => set(r.id, { production: e.target.value === "" ? null : parseFloat(e.target.value) })} className="h-7 rounded-sm text-[11px] font-mono col-span-2" data-testid={`programme-prod-${r.id}`} />
              <Input value={r.crew || ""} placeholder="4 crew · 14t exc" onChange={(e) => set(r.id, { crew: e.target.value })} className="h-7 rounded-sm text-[11px] col-span-2" />
              <span className="font-mono text-xs font-bold" data-testid={`programme-days-${r.id}`}>{dur(r) ? fmtQty(Math.ceil(dur(r))) : "—"}</span>
              <div className="flex items-center gap-0.5 justify-self-end">
                <button onClick={() => move(i, -1)} className="text-zinc-400 hover:text-[#FF5F15]"><ArrowUp className="w-3 h-3" /></button>
                <button onClick={() => move(i, 1)} className="text-zinc-400 hover:text-[#FF5F15]"><ArrowDown className="w-3 h-3" /></button>
                <button onClick={() => patch({ programme: rows.filter((x) => x.id !== r.id).map((x, n) => ({ ...x, order: n })) })} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
          {!rows.length && <p className="px-3 py-5 text-xs text-zinc-500">Add phases in build order — duration comes from quantity ÷ production rate. This carries into the job when you win.</p>}
        </div>
        <div className="px-3 py-2 border-t border-zinc-200 dark:border-white/10 flex justify-between text-xs">
          <span className="text-zinc-500 uppercase font-bold tracking-wider text-[10px]">Estimated duration (sequential)</span>
          <b className="font-mono" data-testid="programme-total-days">{Math.ceil(totalDays)} working days</b>
        </div>
      </Panel>
    </div>
  );
}

// ================= SUPPLIERS TAB =================
export function SuppliersTab({ t, patch }) {
  const enqs = t.enquiries || [];
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {}); }, []);
  const set = (id, ch) => patch({ enquiries: enqs.map((e) => (e.id === id ? { ...e, ...ch } : e)) });
  const add = () => patch({ enquiries: [...enqs, { id: uid(), name: "", measurement_ids: [], quotes: [], status: "requested" }] });
  const ms = t.measurements || [];

  const applyRate = (enq, q) => {
    const eff = (q.rate || 0) + (q.freight || 0);
    patch({
      enquiries: enqs.map((e) => (e.id === enq.id ? { ...e, selected_quote_id: q.id, status: "selected" } : e)),
      measurements: ms.map((m) => (enq.measurement_ids.includes(m.id)
        ? { ...m, rate: { ...(m.rate || {}), unit_rate: eff } } : m)),
    });
    toast.success(`${q.supplier_name} selected — ${nzd(eff)} applied to ${enq.measurement_ids.length} item(s)`);
  };

  return (
    <div className="p-3 max-w-5xl space-y-3" data-testid="takeoff-suppliers-tab">
      <div className="flex justify-end">
        <Button size="sm" onClick={add} className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase" data-testid="suppliers-add-enquiry">
          <Plus className="w-3 h-3 mr-1" /> New pricing enquiry
        </Button>
      </div>
      {enqs.map((enq) => (
        <Panel key={enq.id} title={enq.name || "Untitled enquiry"} testId={`enquiry-${enq.id}`}
          action={<button onClick={() => patch({ enquiries: enqs.filter((x) => x.id !== enq.id) })} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}>
          <div className="p-3 space-y-2">
            <div className="flex gap-2">
              <Input value={enq.name} placeholder="e.g. AP40 supply · DN300 pipe package" onChange={(e) => set(enq.id, { name: e.target.value })} className="h-8 rounded-sm text-xs flex-1" />
              <select value={enq.status} onChange={(e) => set(enq.id, { status: e.target.value })}
                className="h-8 rounded-sm text-[10px] font-bold uppercase bg-transparent border border-zinc-300 dark:border-white/15 px-1 dark:bg-[#0F141C]">
                {["requested", "received", "declined", "selected"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <details className="text-[11px]">
              <summary className="cursor-pointer text-zinc-500 font-bold uppercase tracking-wider text-[10px]">Linked takeoff items ({enq.measurement_ids.length})</summary>
              <div className="mt-1 max-h-36 overflow-y-auto space-y-0.5">
                {ms.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={enq.measurement_ids.includes(m.id)}
                      onChange={(e) => set(enq.id, { measurement_ids: e.target.checked ? [...enq.measurement_ids, m.id] : enq.measurement_ids.filter((x) => x !== m.id) })}
                      className="accent-[#FF5F15]" />
                    {m.name} <span className="font-mono text-zinc-400">{fmtQty(m.qty)} {m.unit}</span>
                  </label>
                ))}
              </div>
            </details>
            {/* supplier quotes comparison */}
            <div className="border border-zinc-200 dark:border-white/10">
              <div className="px-2 py-1 grid grid-cols-12 gap-2 text-[9px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
                <span className="col-span-3">Supplier</span><span className="col-span-2">Rate</span><span className="col-span-2">Freight</span><span>Lead</span><span className="col-span-2">Effective</span><span className="col-span-2" />
              </div>
              {(enq.quotes || []).map((q) => {
                const eff = (q.rate || 0) + (q.freight || 0);
                const sel = enq.selected_quote_id === q.id;
                return (
                  <div key={q.id} className={`px-2 py-1 grid grid-cols-12 gap-2 items-center ${sel ? "bg-emerald-500/10" : ""}`} data-testid={`supplier-quote-${q.id}`}>
                    <select value={q.supplier_name || ""} onChange={(e) => set(enq.id, { quotes: enq.quotes.map((x) => (x.id === q.id ? { ...x, supplier_name: e.target.value } : x)) })}
                      className="col-span-3 h-7 rounded-sm text-[11px] bg-transparent border border-zinc-300 dark:border-white/15 px-1 dark:bg-[#0F141C]">
                      <option value="">Pick supplier…</option>
                      {suppliers.map((s) => <option key={s.id} value={s.trading_name}>{s.trading_name}</option>)}
                    </select>
                    <Input type="number" step="0.01" value={q.rate ?? ""} onChange={(e) => set(enq.id, { quotes: enq.quotes.map((x) => (x.id === q.id ? { ...x, rate: e.target.value === "" ? null : parseFloat(e.target.value) } : x)) })} className="col-span-2 h-7 rounded-sm text-[11px] font-mono" />
                    <Input type="number" step="0.01" value={q.freight ?? ""} onChange={(e) => set(enq.id, { quotes: enq.quotes.map((x) => (x.id === q.id ? { ...x, freight: e.target.value === "" ? null : parseFloat(e.target.value) } : x)) })} className="col-span-2 h-7 rounded-sm text-[11px] font-mono" />
                    <Input value={q.lead || ""} placeholder="3d" onChange={(e) => set(enq.id, { quotes: enq.quotes.map((x) => (x.id === q.id ? { ...x, lead: e.target.value } : x)) })} className="h-7 rounded-sm text-[11px]" />
                    <b className="col-span-2 font-mono text-xs">{nzd(eff)}</b>
                    <div className="col-span-2 flex justify-end gap-1">
                      <Button size="sm" variant={sel ? "default" : "outline"} onClick={() => applyRate(enq, q)}
                        className={`h-6 px-2 rounded-sm text-[9px] font-bold uppercase ${sel ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`} data-testid={`supplier-select-${q.id}`}>
                        {sel ? "Selected" : "Use rate"}
                      </Button>
                      <button onClick={() => set(enq.id, { quotes: enq.quotes.filter((x) => x.id !== q.id) })} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                );
              })}
              <button onClick={() => set(enq.id, { quotes: [...(enq.quotes || []), { id: uid(), supplier_name: "", rate: null, freight: null, lead: "" }] })}
                className="w-full px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#FF5F15]" data-testid={`enquiry-add-quote-${enq.id}`}>
                + Add supplier price
              </button>
            </div>
            <p className="text-[9px] text-zinc-400">"Use rate" writes the effective rate (rate + freight) onto the linked takeoff items — nothing changes until you click it.</p>
          </div>
        </Panel>
      ))}
      {!enqs.length && <p className="text-xs text-zinc-500 text-center py-8">Group items into a pricing enquiry, add supplier prices side by side, then apply the winning rate straight to the estimate.</p>}
    </div>
  );
}

// ================= WIN JOB DIALOG =================
export function WinJobDialog({ open, onClose, t, onWon }) {
  const navigate = useNavigate();
  const [carry, setCarry] = useState({ carry_budget: true, carry_targets: true, carry_programme: true, carry_risks: true });
  const [busy, setBusy] = useState(false);
  const est = computeEstimate(t);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/takeoffs/${t.id}/win-job`, carry);
      toast.success(`Job ${data.job_number} created — budget ${nzd(data.budget_total)}, ${data.targets} production targets`, {
        action: { label: "Open job", onClick: () => navigate(`/app/jobs/${data.job_id}`) },
      });
      onWon(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Win job failed"); }
    finally { setBusy(false); }
  };
  const Row = ({ k, label, desc }) => (
    <label className="flex items-start gap-2 text-xs cursor-pointer py-1">
      <input type="checkbox" checked={carry[k]} onChange={(e) => setCarry({ ...carry, [k]: e.target.checked })} className="accent-[#FF5F15] mt-0.5" data-testid={`winjob-${k}`} />
      <span><b>{label}</b><span className="block text-[10px] text-zinc-500">{desc}</span></span>
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="winjob-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#FF5F15]" /> Win Job — carry the estimate forward
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500">Creates the Fieldlink job from this takeoff so nothing is re-entered. Estimated sell price: <b className="text-[#FF5F15]">{nzd(est.sell)}</b></p>
        <div className="space-y-0.5">
          <Row k="carry_budget" label="Estimate budget" desc="Discipline totals become the job budget / cost codes" />
          <Row k="carry_targets" label="Production targets" desc="Measured quantities become install targets (target vs installed)" />
          <Row k="carry_programme" label="Tender programme" desc="Phases, durations and crew notes carry onto the job" />
          <Row k="carry_risks" label="Risk register" desc="Tender risks carry over for the site team" />
        </div>
        <Button onClick={run} disabled={busy} className="w-full h-9 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="winjob-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "🏆 Create Project"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
